import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaUserPlus, FaEye, FaTrash } from 'react-icons/fa';
import DataTable from '../shared/DataTable';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import Loader from '../../shared/Loader';
import { showError, showSuccess, showConfirm } from '../../../utils/Alert';
import './UserManagement.css';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Drawer Management
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [isViewOnly, setIsViewOnly] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/admin/users', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setUsers(res.data);
            setTimeout(() => {
                setLoading(false);
            }, 500);
        } catch (err) {
            console.error(err);
            showError('Data Sync Error', 'Failed to retrieve user directory.');
            setTimeout(() => {
                setLoading(false);
            }, 500);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleView = (user) => {
        setIsViewOnly(true);
        setEditUser(user);
    };

    const handleEdit = (user) => {
        setIsViewOnly(false);
        setEditUser(user);
    };

    const handleDelete = async (user) => {
        if (user.role === 'super_admin') {
            return showError('Access Denied', 'Super Administrator accounts cannot be deleted.');
        }

        const result = await showConfirm('Permanent Purge?', `Are you absolutely sure you want to delete ${user.name}? This action is irreversible.`);
        if (result.isConfirmed) {
            try {
                await axios.delete(`/api/admin/users/${user._id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                });
                showSuccess('Purged', 'User has been permanently removed.');
                fetchUsers();
            } catch (err) {
                showError('Deletion Failed', 'System was unable to purge this entity.');
            }
        }
    };

    const columns = [
        { header: 'User Hash', key: '_id', width: '15%', render: (row) => <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-muted)' }}>{row._id}</span> },
        {
            header: 'Identity',
            key: 'name',
            width: '20%',
            render: (row) => (
                <div style={{ fontWeight: 600, color: 'var(--primary-blue)' }}>{row.name}</div>
            )
        },
        { header: 'Email Address', key: 'email', width: '25%' },
        {
            header: 'Privilege Level',
            key: 'role',
            width: '15%',
            render: (row) => (
                <span className={`role-badge role-${row.role}`}>
                    {row.role.replace('_', ' ').toUpperCase()}
                </span>
            )
        },
        {
            header: 'Status',
            key: 'status',
            width: '10%',
            render: (row) => {
                const isActive = row.status !== 'inactive';
                return (
                    <span className={`status-badge ${isActive ? 'active' : 'inactive'}`} style={{ backgroundColor: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: isActive ? '#10B981' : '#EF4444' }}>
                        {isActive ? 'ACTIVE' : 'SUSPENDED'}
                    </span>
                );
            }
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '15%',
            render: (row) => (
                <div className="actions-cell">
                    <button className="icon-btn view-btn" title="View Intelligence" onClick={() => handleView(row)}>
                        <FaEye />
                    </button>
                    <button className="icon-btn edit-btn" title="Configure Credentials" onClick={() => handleEdit(row)}>
                        <FaEdit />
                    </button>
                    {row.role !== 'super_admin' && (
                        <button className="icon-btn delete-btn" title="Purge Entity" onClick={() => handleDelete(row)}>
                            <FaTrash />
                        </button>
                    )}
                </div>
            )
        }
    ];

    const filterOptions = [
        { label: 'Academic Students', value: 'student' },
        { label: 'Talent Seekers', value: 'job_seeker' },
        { label: 'Tutors / Instructors', value: 'tutor' },
        { label: 'Recruiters', value: 'recruiter' },
        { label: 'Super Admins', value: 'super_admin' },
        { label: 'Active Access', value: 'active' },
        { label: 'Suspended', value: 'inactive' }
    ];

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="user-management fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Identity & Access Management</h1>
                <p className="page-subtitle">Synchronizing enterprise user roles and permission matrices.</p>
            </div>

            <DataTable
                title="System Directory"
                columns={columns}
                data={users}
                filterOptions={filterOptions}
                actions={
                    <button className="primary-btn pulse-on-hover" onClick={() => setIsAddOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaUserPlus /> Provision Account
                    </button>
                }
            />

            {/* Overlays */}
            <AddUserModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} onUserAdded={fetchUsers} />
            <EditUserModal isOpen={!!editUser} onClose={() => setEditUser(null)} user={editUser} onUserUpdated={fetchUsers} isViewOnly={isViewOnly} />
        </div>
    );
};

export default UserManagement;
